import {
    datesAreConsecutive,
    getTimezoneOffsetDate,
    getEmptySelectionSet,
    getEmptySelectionSets,
    getStatementListLabel,
    getRegionFilterFromStatementId,
    isInYearToDate,
} from '@trqk/utils';

export const getStatementSelectionId = (
    userId: string,
    entityId: string,
): string => `user:${userId}.entity:${entityId}`;

interface GetSelectionLabelInput {
    statementViews: TrqkStatementView[];
    labelKey: SelectionLabelKey;
    context: StatementSelectionContext;
}

/**
 * Returns an object with a .short and .long property. The .long property is generated by
 * getStatementListLabel(); the .short property is just mapped from the second argument.
 * @param statementViews array of TrqkStatementView objects, orderer by end_date (earliest
 *      first)
 * @param short Default name for selection
 */
export const getSelectionLabel = ({
    statementViews,
    labelKey,
    context,
}: GetSelectionLabelInput): SelectionLabel => ({
    long: getStatementListLabel(statementViews, context.shortLabels[labelKey]),
    short: context.shortLabels[labelKey],
});

/**
 * Creates a Map from an array of TrqkStatmentViewPartial elements, where each key is a statementId
 * and each value is a TrqkStatementView
 * @param statementViews
 */
export const createStatementViewsMap = (
    statementViews: TrqkStatementView[],
): StatementViewsMap => {
    const tuples = statementViews.reduce(
        (
            acc: StatementViewMapTuple[],
            statementView: TrqkStatementView,
        ): StatementViewMapTuple[] => {
            if (statementView.statement_id) {
                acc.push([statementView.statement_id, statementView]);
            }

            return acc;
        },
        [] as StatementViewMapTuple[],
    );

    return new Map(tuples);
};

/**
 * Given the statementViewsMap of all statements, map it to a statementViewsMap consisting of the
 * contiguous range that starts with the most recent statement.
 * @param statementViewsMap the Map of all statementViews for this userId / entityId combination,
 * sorted by statementView.end_date (earliest first)
 */
export const getMostRecentContiguousRange = (
    statementViewsMap: StatementViewsMap,
): StatementViewsMap => {
    const statementViewsReversed = Array.from(
        statementViewsMap.values(),
    ).reverse();

    const tuples = [] as StatementViewMapTuple[];

    statementViewsReversed.forEach((statementView: TrqkStatementView): void => {
        const lastStatement = tuples?.[tuples.length - 1]?.[1] ?? null;

        if (statementView.statement_id) {
            if (lastStatement === null) {
                tuples.unshift([statementView.statement_id, statementView]);
            } else {
                const lastStatementStart = getTimezoneOffsetDate(
                    lastStatement?.start_date ?? null,
                );
                const currentStatementEnd = getTimezoneOffsetDate(
                    statementView?.end_date ?? null,
                );

                if (
                    lastStatementStart !== null &&
                    currentStatementEnd !== null &&
                    datesAreConsecutive(currentStatementEnd, lastStatementStart)
                ) {
                    tuples.unshift([statementView.statement_id, statementView]);
                }
            }
        }
    });

    return new Map(tuples);
};

const regionFilterList = [
    'unfiltered' as PRORegionFilter,
    'international' as PRORegionFilter,
    'standard' as PRORegionFilter,
];

/**
 * Gets the selection sets for a given context. This is used when first setting up a
 * statementSelectionMachine, as well as when adding or deleting a statement.
 * @param {GetInitialSelectionSetsInput} input
 * @returns {StatementSelectionContext}
 */
export const getSelectionSets = ({
    context,
    initializers,
}: GetInitialSelectionSetsInput): StatementSelectionContext => {
    const { statementViewsMap } = context;
    const nextSelectionSets = getEmptySelectionSets();

    // These lists will be used later to generate selectionLabels
    const statementViewLists = {
        international: {
            presets: {
                all: [] as TrqkStatementView[],
                lastTwo: [] as TrqkStatementView[],
                yearToDate: [] as TrqkStatementView[],
            },
        },
        standard: {
            presets: {
                all: [] as TrqkStatementView[],
                lastTwo: [] as TrqkStatementView[],
                yearToDate: [] as TrqkStatementView[],
            },
        },
        unfiltered: {
            presets: {
                all: [] as TrqkStatementView[],
                lastTwo: [] as TrqkStatementView[],
                yearToDate: [] as TrqkStatementView[],
            },
        },
    };

    /**
     * When iterating through statementViewsMap, this function is used to
     * programmatically set the various properties of each of the selectionSets and
     * statementViewLists
     * @param region region of the current statementView
     * @param regionFilter one of the three region filters, 'unfiltered', 'standard'
     *      or 'international'
     * @param inThisYear whether the current statementView is in the current year
     * @param statementView the statementView itself
     * @param statementId the statementView.statement_id
     */
    const mutateSelectionSetsAndStatementViewLists = (
        region: ProRegionType | null,
        regionFilter: PRORegionFilter,
        inThisYear: boolean,
        statementView: TrqkStatementView,
        statementId: string,
    ): void => {
        if (region === regionFilter || regionFilter === 'unfiltered') {
            nextSelectionSets[regionFilter].selectableViews.push(statementView);

            nextSelectionSets[regionFilter].presets.all.ids.push(statementId);

            statementViewLists[regionFilter].presets.all.push(statementView);

            if (inThisYear) {
                nextSelectionSets[regionFilter].presets.yearToDate.ids.push(
                    statementId,
                );

                statementViewLists[regionFilter].presets.yearToDate.push(
                    statementView,
                );
            }
        }
    };

    // Generate data for each of the selectionSets by iterating through
    // statementViewsMap
    statementViewsMap.forEach((statementView: TrqkStatementView): void => {
        const { statement_id, start_date, region } = statementView;

        const inThisYear = isInYearToDate(start_date);

        if (statement_id) {
            for (const regionFilter of regionFilterList) {
                mutateSelectionSetsAndStatementViewLists(
                    region ?? null,
                    regionFilter,
                    inThisYear,
                    statementView,
                    statement_id,
                );
            }
        }
    });

    for (const regionFilter of regionFilterList) {
        // If there are at least two statements, set lists for `lastTwo` to the latest two
        // statements
        if (statementViewLists[regionFilter].presets.all.length > 1) {
            nextSelectionSets[regionFilter].presets.lastTwo.ids =
                nextSelectionSets[regionFilter].presets.all.ids.slice(0, 2);

            statementViewLists[regionFilter].presets.lastTwo =
                statementViewLists[regionFilter].presets.all.slice(0, 2);
        }

        // Generate selection labels for each of the selectionSets
        nextSelectionSets[regionFilter].label = getSelectionLabel({
            statementViews: [],
            labelKey: 'selected',
            context,
        });

        nextSelectionSets[regionFilter].presets.all.label = getSelectionLabel({
            statementViews: statementViewLists[regionFilter].presets.all,
            labelKey: 'all',
            context,
        });

        nextSelectionSets[regionFilter].presets.lastTwo.label =
            getSelectionLabel({
                statementViews:
                    statementViewLists[regionFilter].presets.lastTwo,
                labelKey: 'lastTwo',
                context,
            });

        nextSelectionSets[regionFilter].presets.yearToDate.label =
            getSelectionLabel({
                statementViews:
                    statementViewLists[regionFilter].presets.yearToDate,
                labelKey: 'yearToDate',
                context,
            });
    }

    const nextContext = {
        ...context,
    };

    // `initializers`, if provided, will be an array of SelectionSetInitializer objects. Each
    // value is used to set the current regionFilter and selectedIds for that respective
    // StatementSelectorState.
    if (initializers) {
        initializers.forEach((initializer: SelectionSetInitializer): void => {
            const { statementSelectorState } = initializer;
            const initialRegion = initializer?.initialRegion ?? null;
            const initialSelectedIds = initializer?.initialSelectedIds ?? null;

            if (initialRegion !== null && initialSelectedIds !== null) {
                const selectionSets = { ...nextSelectionSets };
                const regionFilter = initialRegion;

                const initialStatementViews = initialSelectedIds.reduce(
                    (
                        acc: TrqkStatementView[],
                        currentStatementId: string,
                    ): TrqkStatementView[] => {
                        const currentStatementView =
                            statementViewsMap.get(currentStatementId);

                        if (currentStatementView) {
                            acc.push(currentStatementView);
                        }

                        return acc;
                    },
                    [],
                );

                selectionSets[regionFilter].selectedIds = initialSelectedIds;

                selectionSets[regionFilter].label = getSelectionLabel({
                    statementViews: initialStatementViews,
                    labelKey: regionFilter,
                    context,
                });

                const selectorState: StatementSelectorState = {
                    regionFilter,
                    selectionSets,
                };

                if (statementSelectorState === 'saved') {
                    nextContext.saved = selectorState;
                }

                if (statementSelectorState === 'editing') {
                    nextContext.editing = selectorState;
                }
            }
        });
    }

    return {
        ...nextContext,
        ready: true,
    };
};

export const getEmptySelectorState = (): StatementSelectorState => ({
    regionFilter: 'standard',
    selectionSets: {
        international: getEmptySelectionSet(),
        standard: getEmptySelectionSet(),
        unfiltered: getEmptySelectionSet(),
    },
});

export const getEmptyStatementSelectionContext =
    (): StatementSelectionContext => ({
        // id is composed of the userId and entityId
        id: '',
        editing: null,
        // Within current, .ids is the list of currently selected ids, while .views
        // is the list of currently selectable views, and .label is the label for the currently
        // selected ids.
        entityId: '',
        modifiedAt: 0,
        pro: null,
        ready: false,
        saved: getEmptySelectorState(),
        shortLabels: {
            all: '',
            checked: '',
            international: '',
            lastTwo: '',
            selected: '',
            standard: '',
            unfiltered: '',
            yearToDate: '',
        },
        // A Map of statementViews using statementView.statement_id as the key and the
        // statementView as the value, sorted by statementView.end_date (earliest first)
        statementViewsMap: new Map(),
        userId: '',
    });

interface GetInitialSelectionInput {
    /**
     * Current value of context.statementViewsMap
     */
    statementViewsMap: StatementViewsMap;
    /**
     * Selected statement IDs that were pulled from the URL param `statement_ids`
     */
    urlParamSelectedIds: string[] | null;
}

/**
 * Calculate initialRegion and initialSelectedIds.
 *  - If urlParamSelectedIds is provided:
 *      - uses the urlParamSelectedIds to determine the initialSelectedIds
 *      - uses the first urlParamSelectedIds to determine the initialRegion
 *
 *  - If urlParamSelectedIds is not provided:
 *      - Sets initialSelectedIds to most recent domestic statement, unless there are only
 *          international statements - then uses most recent international statement
 *      - Sets initialRegion accordingly
 * @param completedStatementViews
 */
export const getInitialSelection = ({
    statementViewsMap,
    urlParamSelectedIds,
}: GetInitialSelectionInput): InitialStatementSelection => {
    const sortedDomesticViews: TrqkStatementView[] = [];
    const sortedInternationalViews: TrqkStatementView[] = [];

    statementViewsMap.forEach((view: TrqkStatementView) => {
        if (view.region === 'standard') {
            sortedDomesticViews.push(view);
        }

        if (view.region === 'international') {
            sortedInternationalViews.push(view);
        }
    });

    let initialSelectedIds: string[] = [];
    let initialRegion: PRORegionFilter = 'standard';

    if (urlParamSelectedIds !== null && urlParamSelectedIds.length > 0) {
        // If possible, use the first ID in `urlParamSelectedIds` to determine the region
        initialRegion = getRegionFilterFromStatementId(urlParamSelectedIds[0]);
    }

    const currentSortedViews =
        initialRegion === 'standard'
            ? sortedDomesticViews
            : sortedInternationalViews;

    // These indexes are coerced to -1 if the urlParamSelectedIds does not contain an ID that can be
    // found within the `currentSortedViews`, so that we can rely on them for later if/else logic
    const indexOfFirstSelectedId =
        urlParamSelectedIds === null || urlParamSelectedIds.length < 1
            ? -1
            : currentSortedViews.findIndex(
                  ({ statement_id }: TrqkStatementView): boolean =>
                      statement_id !== null &&
                      statement_id === urlParamSelectedIds[0],
              );
    const indexOfLastSelectedId =
        urlParamSelectedIds === null || urlParamSelectedIds.length < 2
            ? -1
            : currentSortedViews.findIndex(
                  ({ statement_id }: TrqkStatementView): boolean =>
                      statement_id !== null &&
                      statement_id === urlParamSelectedIds[1],
              );

    // If we have at least one ID in `urlParamSelectedIds` that can be found in
    // `currentSortedViews`, proceed with determining values from `urlParamSelectedIds`
    if (
        urlParamSelectedIds &&
        urlParamSelectedIds.length > 0 &&
        indexOfFirstSelectedId > -1
    ) {
        if (urlParamSelectedIds.length === 1) {
            // If there is only one ID in `urlParamSelectedIds`, use it as the selected ID
            initialSelectedIds = urlParamSelectedIds;
        } else if (indexOfLastSelectedId < 0) {
            // If the second ID in `urlParamSelectedIds` is not present in `currentSortedViews`,
            // fall back to using the first id in `urlParamSelectedIds` as the selected ID
            initialSelectedIds = [urlParamSelectedIds[0]];
        } else {
            // Otherwise, use `indexOfFirstSelectedId` and `indexOfLastSelectedId` to select the
            // range of statement IDs that fall between the two
            initialSelectedIds = currentSortedViews.reduce(
                (
                    acc: string[],
                    curr: TrqkStatementView,
                    index: number,
                ): string[] => {
                    if (
                        curr.statement_id &&
                        index >= indexOfFirstSelectedId &&
                        index <= indexOfLastSelectedId
                    ) {
                        acc.push(curr.statement_id);
                    }

                    return acc;
                },
                [],
            );
        }
        // If there are no valid statementIds in `urlParamSelectedIds`, determine values from sorted
        // statement views
    } else if (
        sortedDomesticViews.length > 0 &&
        sortedDomesticViews[0].statement_id
    ) {
        initialSelectedIds = [sortedDomesticViews[0].statement_id];
        initialRegion = 'standard';
    } else if (
        sortedDomesticViews.length === 0 &&
        sortedInternationalViews.length > 0 &&
        sortedInternationalViews[0].statement_id
    ) {
        initialSelectedIds = [sortedInternationalViews[0].statement_id];
        initialRegion = 'international';
    }

    return {
        initialRegion,
        initialSelectedIds,
    };
};
